import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ensurePortalRoot } from '../shared/index.js';
import { useFloating } from '../../hooks/useFloating.js';
import { useOutsideClick } from '../../hooks/useOutsideClick.js';
import { useEscapeStack } from '../../hooks/useEscapeStack.js';
import { Menu } from './Menu.js';
import type { MenuEntry, MenuItemDescriptor } from './types.js';

export interface ContextMenuPanelProps {
  /** Whether the panel is currently shown. */
  readonly open: boolean;
  /** Viewport coordinates the panel anchors to. Ignored when `open` is
   *  false. May be `null` while the host is computing — the panel won't
   *  render until a position is supplied. */
  readonly position: { readonly x: number; readonly y: number } | null;
  readonly items: readonly MenuEntry[];
  /** Closes the panel. The host updates its own state. */
  readonly onClose: () => void;
  /** Fires after a leaf item is activated. The host typically calls
   *  `onClose` from this callback (the wrapper-mode `<ContextMenu>` does
   *  exactly that). */
  readonly onItemSelect?: (item: MenuItemDescriptor) => void;
  /** Outside-pointer events flow through this filter — return `true` to
   *  IGNORE the event (don't close). Useful for the wrapping
   *  `<ContextMenu>` to ignore the right-click that's about to reposition
   *  the menu, avoiding an unmount/remount flicker. */
  readonly shouldIgnoreOutsideEvent?: (event: Event) => boolean;
  /** Close the panel on scroll OUTSIDE its own scrollable region — matches
   *  native OS context menus, which dismiss when the user scrolls the
   *  underlying content. Default `true`. */
  readonly closeOnScroll?: boolean;
  readonly 'aria-label'?: string;
  readonly className?: string;
}

/** Controlled context-menu panel — the host owns `open` and `position`,
 *  and supplies `onClose`. Use this when the right-click open trigger
 *  lives outside the menu's wrapper (e.g. an editor plugin that watches
 *  for `contextmenu` events at the document level and decides per-block
 *  what items to show).
 *
 *  For the simpler "wrap-this-region" case, use `<ContextMenu>` instead
 *  — it owns its own open state and feeds this panel internally. */
export function ContextMenuPanel(props: ContextMenuPanelProps): ReactNode {
  const {
    open,
    position,
    items,
    onClose,
    onItemSelect,
    shouldIgnoreOutsideEvent,
    closeOnScroll = true,
    'aria-label': ariaLabel,
    className,
  } = props;

  const panelRef = useRef<HTMLDivElement | null>(null);

  const reactId = useId();
  const menuId = `${reactId}-ctxmenu`;

  const isLive = open && position !== null && items.length > 0;

  const { setFloating, floatingStyle, maxHeight } = useFloating({
    open: isLive,
    placement: 'bottom-start',
    gap: 0,
    virtualAnchor:
      position !== null
        ? { top: position.y, left: position.x, width: 0, height: 0 }
        : null,
  });

  // Outside click closes the panel — but defer to the host's filter
  // (e.g. the wrapping <ContextMenu> uses this to ignore right-clicks
  // that will re-open it at a new position).
  useOutsideClick(
    panelRef,
    useCallback(
      (e: Event) => {
        if (shouldIgnoreOutsideEvent?.(e) === true) return;
        onClose();
      },
      [onClose, shouldIgnoreOutsideEvent],
    ),
    { enabled: isLive },
  );

  // Escape via the shared modal stack — only the topmost layer responds.
  useEscapeStack(onClose, isLive);

  // Close on scroll OUTSIDE the panel — matches native OS context menus.
  // Scrolls INSIDE the menu (long list with its own scrollbar) are ignored.
  // Capture phase because scroll events don't bubble.
  useEffect(() => {
    if (!isLive || !closeOnScroll) return undefined;
    const handler = (e: Event): void => {
      const panel = panelRef.current;
      const target = e.target;
      if (panel !== null && target instanceof Node && panel.contains(target)) return;
      onClose();
    };
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('scroll', handler, true);
    };
  }, [isLive, closeOnScroll, onClose]);

  const handleSelect = useCallback(
    (item: MenuItemDescriptor): void => {
      onItemSelect?.(item);
      onClose();
    },
    [onItemSelect, onClose],
  );

  const mergedPanelRef = (node: HTMLDivElement | null): void => {
    panelRef.current = node;
    setFloating(node);
  };

  const portalRoot = ensurePortalRoot();
  if (!isLive || portalRoot === null) return null;

  const wrapperClasses = ['vq-context-menu-portal', className].filter(Boolean).join(' ');

  return createPortal(
    <div
      ref={mergedPanelRef}
      id={menuId}
      className={wrapperClasses}
      data-component="ContextMenu"
      style={
        {
          ...floatingStyle,
          ...(maxHeight > 0
            ? { ['--vq-menu-max-h' as string]: `${String(maxHeight)}px` }
            : {}),
        } as Record<string, string | number>
      }
    >
      <Menu
        items={items}
        onItemSelect={handleSelect}
        autoFocus
        {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      />
    </div>,
    portalRoot,
  );
}
